/**
 * Report Post-Processing Pipeline
 * 
 * This utility normalizes and standardizes LLM-generated reports to ensure
 * consistent formatting regardless of which LLM was used to generate the content.
 */

// Emoji mapping for consistent rendering
const EMOJI_MAPPINGS = {
  // Chart emojis
  ':chart_with_upwards_trend:': '📈',
  ':chart_with_downwards_trend:': '📉',
  ':bar_chart:': '📊',
  
  // Status indicators
  ':white_check_mark:': '✅',
  ':warning:': '⚠️',
  ':x:': '❌',
  ':red_circle:': '🔴',
  ':green_circle:': '🟢',
  ':yellow_circle:': '🟡',
  
  // Awards and recognition
  ':first_place_medal:': '🥇',
  ':second_place_medal:': '🥈',
  ':third_place_medal:': '🥉',
  ':star2:': '🌟',
  ':star:': '⭐',
  
  // Weather
  ':mostly_sunny:': '🌤️',
  ':sunny:': '☀️',
  ':partly_sunny:': '⛅',
  ':cloud:': '☁️',
  ':rain_cloud:': '🌧️',
  ':snow_cloud:': '🌨️',
  
  // Flags and alerts
  ':triangular_flag_on_post:': '🚩',
  ':pushpin:': '📌',
  ':stopwatch:': '⏱️',
  ':bell:': '🔔',
  ':lock:': '🔒',
  ':no_entry_sign:': '🚫',
  
  // Gestures and people
  ':wave:': '👋',
  ':thumbs_up:': '👍',
  ':thumbs_down:': '👎',
  
  // Objects
  ':mag:': '🔍',
  ':memo:': '📝',
  ':clipboard:': '📋',
  ':calendar:': '📅'
};

/**
 * Converts emoji shortcodes to actual emoji characters
 * @param {string} text - Text containing emoji shortcodes
 * @returns {string} Text with emoji characters
 */
function standardizeEmojis(text) {
  let processedText = text;
  
  // Replace emoji shortcodes with actual emojis
  Object.entries(EMOJI_MAPPINGS).forEach(([shortcode, emoji]) => {
    const regex = new RegExp(shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    processedText = processedText.replace(regex, emoji);
  });
  
  return processedText;
}

/**
 * Normalizes markdown formatting inconsistencies
 * @param {string} text - Raw markdown text
 * @returns {string} Normalized markdown text
 */
function normalizeMarkdown(text) {
  let normalized = text;
  
  // Standardize header formatting
  normalized = normalized.replace(/^#{1,6}\s*/gm, (match) => {
    const level = match.trim().length;
    return '#'.repeat(level) + ' ';
  });
  
  // Standardize bullet points
  normalized = normalized.replace(/^[\s]*[-*+]\s+/gm, '• ');
  
  // Standardize numbered lists
  normalized = normalized.replace(/^[\s]*\d+\.\s+/gm, (match, offset, string) => {
    const lineStart = string.lastIndexOf('\n', offset) + 1;
    const indent = offset - lineStart;
    return ' '.repeat(indent) + '1. ';
  });
  
  // Clean up excessive whitespace
  normalized = normalized.replace(/\n{3,}/g, '\n\n');
  normalized = normalized.replace(/[ \t]+$/gm, ''); // Remove trailing spaces
  
  // Standardize table formatting
  normalized = normalizeTableFormatting(normalized);
  
  return normalized;
}

/**
 * Normalizes table formatting to ensure consistent display
 * @param {string} text - Text containing markdown tables
 * @returns {string} Text with normalized tables
 */
function normalizeTableFormatting(text) {
  // Find and normalize markdown tables
  const tableRegex = /(\|[^\n]+\|[\s]*\n)+/g;
  
  return text.replace(tableRegex, (tableMatch) => {
    const lines = tableMatch.trim().split('\n');
    const normalizedLines = [];
    
    lines.forEach((line, index) => {
      if (line.trim()) {
        // Clean up table row formatting
        let cleanLine = line.trim();
        
        // Ensure proper spacing around pipes
        cleanLine = cleanLine.replace(/\s*\|\s*/g, ' | ');
        
        // Ensure line starts and ends with pipe
        if (!cleanLine.startsWith('|')) cleanLine = '| ' + cleanLine;
        if (!cleanLine.endsWith('|')) cleanLine = cleanLine + ' |';
        
        normalizedLines.push(cleanLine);
        
        // Add separator row after header if not present
        if (index === 0 && lines.length > 1 && !lines[1].includes('---')) {
          const cellCount = (cleanLine.match(/\|/g) || []).length - 1;
          const separator = '| ' + Array(cellCount).fill('---').join(' | ') + ' |';
          normalizedLines.push(separator);
        }
      }
    });
    
    return normalizedLines.join('\n') + '\n\n';
  });
}

/**
 * Enforces consistent section structure based on expected report format
 * @param {string} text - Report text
 * @returns {string} Structured report text
 * @deprecated This function should not be used as it enforces specific sections
 */
function enforceStructure(text) {
  // Removed section enforcement per code review feedback
  // We should not be enforcing expected sections
  return text;
}

/**
 * Sanitizes and cleans up common formatting artifacts
 * @param {string} text - Raw text from LLM
 * @returns {string} Cleaned text
 */
function sanitizeResponse(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  let cleaned = text;
  
  // Only do minimal, safe cleanup - don't edit actual content
  // Just clean up excessive whitespace
  cleaned = cleaned.replace(/\n{4,}/g, '\n\n\n'); // Limit to max 3 newlines
  cleaned = cleaned.replace(/[ \t]+$/gm, ''); // Remove trailing spaces
  
  return cleaned.trim();
}

/**
 * Validates that the report contains expected sections and data
 * @param {string} text - Processed report text
 * @returns {Object} Validation result with warnings
 */
function validateReportStructure(text) {
  const warnings = [];
  
  // Removed section validation per code review feedback
  // We should not be enforcing expected sections
  
  // Only check for basic content length
  if (text.length < 50) {
    warnings.push('Content appears to be very short');
  }
  
  return {
    isValid: warnings.length === 0,
    warnings
  };
}

/**
 * Main post-processing function that applies all transformations
 * @param {string} rawResponse - Raw response from LLM
 * @param {Object} options - Processing options
 * @returns {Object} Processed response with metadata
 */
export function processReportResponse(rawResponse, options = {}) {
  // Handle null/undefined input gracefully
  if (rawResponse === null || rawResponse === undefined) {
    return {
      processedText: null,
      originalText: null,
      error: 'Input is null or undefined',
      processingSteps: [],
      validation: { isValid: false, warnings: ['Input is null or undefined'] },
      metadata: { originalLength: 0, processedLength: 0, emojiCount: 0, processingTime: Date.now() }
    };
  }
  
  const {
    enableEmojiStandardization = true,
    enableMarkdownNormalization = true,
    enableStructureEnforcement = false, // Disabled by default per code review
    enableSanitization = true,
    enableValidation = false // Disabled by default per code review
  } = options;
  
  let processed = rawResponse;
  const processingSteps = [];
  
  try {
    // Step 1: Sanitize response
    if (enableSanitization) {
      processed = sanitizeResponse(processed);
      processingSteps.push('sanitization');
    }
    
    // Step 2: Standardize emojis
    if (enableEmojiStandardization) {
      processed = standardizeEmojis(processed);
      processingSteps.push('emoji_standardization');
    }
    
    // Step 3: Normalize markdown
    if (enableMarkdownNormalization) {
      processed = normalizeMarkdown(processed);
      processingSteps.push('markdown_normalization');
    }
    
    // Step 4: Enforce structure
    if (enableStructureEnforcement) {
      processed = enforceStructure(processed);
      processingSteps.push('structure_enforcement');
    }
    
    // Step 5: Validate result
    let validation = { isValid: true, warnings: [] };
    if (enableValidation) {
      validation = validateReportStructure(processed);
      processingSteps.push('validation');
    }
    
    return {
      processedText: processed,
      originalText: rawResponse,
      processingSteps,
      validation,
      metadata: {
        originalLength: rawResponse.length,
        processedLength: processed.length,
        emojiCount: (processed.match(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{2B50}-\u{2B50}]/gu) || []).length,
        processingTime: Date.now()
      }
    };
    
  } catch (error) {
    console.error('Error in report post-processing:', error);
    
    return {
      processedText: rawResponse, // Return original on error
      originalText: rawResponse,
      processingSteps: [],
      validation: { isValid: false, warnings: [`Processing error: ${error.message}`] },
      error: error.message,
      metadata: {
        originalLength: rawResponse.length,
        processedLength: rawResponse.length,
        emojiCount: 0,
        processingTime: Date.now()
      }
    };
  }
}

/**
 * Utility function to get processing statistics
 * @param {Object} processedResult - Result from processReportResponse
 * @returns {Object} Processing statistics
 */
export function getProcessingStats(processedResult) {
  const { metadata, validation, processingSteps } = processedResult;
  
  return {
    lengthChange: metadata.processedLength - metadata.originalLength,
    lengthChangePercent: ((metadata.processedLength - metadata.originalLength) / metadata.originalLength * 100).toFixed(2),
    emojiCount: metadata.emojiCount,
    stepsApplied: processingSteps.length,
    isValid: validation.isValid,
    warningCount: validation.warnings.length
  };
}

// Export individual functions for use in other components
export {
  standardizeEmojis,
  normalizeMarkdown,
  enforceStructure,
  sanitizeResponse,
  validateReportStructure
};

export default {
  processReportResponse,
  getProcessingStats,
  standardizeEmojis,
  normalizeMarkdown,
  enforceStructure,
  sanitizeResponse,
  validateReportStructure
};
